import { z } from 'zod';
import { RateNode } from './rate-node-schema';

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for rate history - accepts various formats and coerces to standard
 * Handles both legacy array format and current {initial, history} object format
 */
const lenientRateHistoryNodeSchema = z.unknown().transform((val): RateHistoryNode => {
  // Handle null/undefined - return default
  if (val === null || val === undefined) {
    return {
      initial: { date: '', rate: 0 },
      history: []
    };
  }
  
  // Handle legacy array format: convert to {initial, history}
  if (Array.isArray(val)) {
    const rates = val.map(item => RateNode.deserialize(item));
    if (rates.length === 0) {
      return {
        initial: { date: '', rate: 0 },
        history: []
      };
    }
    // First rate becomes initial, rest become history
    return {
      initial: rates[0],
      history: rates.slice(1)
    };
  }
  
  // Handle object format
  if (typeof val === 'object') {
    const obj = val as any;
    return {
      initial: obj.initial ? RateNode.deserialize(obj.initial) : { date: '', rate: 0 },
      history: Array.isArray(obj.history) 
        ? obj.history.map((item: unknown) => RateNode.deserialize(item))
        : []
    };
  }
  
  // Fallback to default
  return {
    initial: { date: '', rate: 0 },
    history: []
  };
});

/**
 * Strict schema for rate history - enforces exact format
 */
const strictRateHistoryNodeSchema = z.object({
  initial: z.unknown().transform(val => RateNode.deserialize(val)),
  history: z.array(z.unknown().transform(val => RateNode.deserialize(val))),
}).transform(val => ({
  initial: val.initial,
  history: val.history
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface RateHistoryNode {
  initial: RateNode;
  history: RateNode[];
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace RateHistoryNode {
  /**
   * Safely deserialize unknown data into RateHistoryNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): RateHistoryNode {
    return lenientRateHistoryNodeSchema.parse(data);
  }

  /**
   * Create a new RateHistoryNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: RateHistoryNode): RateHistoryNode {
    return strictRateHistoryNodeSchema.parse(data);
  }

  /**
   * Create a default RateHistoryNode with 0% initial rate
   */
  export function createDefault(): RateHistoryNode {
    return {
      initial: { date: '', rate: 0 },
      history: []
    };
  }

  /**
   * Validate that data conforms to RateHistoryNode structure
   */
  export function validate(data: unknown): data is RateHistoryNode {
    return strictRateHistoryNodeSchema.safeParse(data).success;
  }

  /**
   * Get the current rate (most recent in history or initial if no history)
   */
  export function getCurrentRate(node: RateHistoryNode): RateNode {
    if (node.history.length === 0) return node.initial;
    return node.history[node.history.length - 1];
  }

  /**
   * Get all rates sorted by date (oldest to newest)
   */
  export function getAllRatesSorted(node: RateHistoryNode): RateNode[] {
    return [node.initial, ...node.history].sort((a, b) => a.date.localeCompare(b.date));
  }
}
